/**
 * 중점 분석 «영역» 그리기의 좌표 수학 — **순수 함수만**, 한 벌.
 *
 * 이 파일이 따로 있는 이유는 사진 위 좌표 변환 사본이 이 레포에 이미 둘 있기 때문이다:
 * `usePhotoAnnotation`(그리기 메모 — rect 기준 절대 좌표 + clamp)과
 * `InteractiveHazardEditor`(발견사항 박스 — `containerWidth`로 나눈 **델타**). 영역 그리기는
 * **절대 좌표**가 필요해(두 점이 사각형을 만든다) 후자를 그대로 쓸 수 없고, 그렇다고 컴포넌트
 * 안에 셋째 벌을 두면 다음 사람이 어느 것이 정본인지 모른다. 그래서 수학만 떼어 두고
 * `AnalysisRegionEditor` 하나가 쓴다 — 두 진입(업로드 미리보기·사진 상세)이 그 컴포넌트를
 * 재사용하므로 변환 사본은 늘지 않는다.
 *
 * ⚠️ 여기서 다루는 좌표는 전부 **표시 방향 기준 정규화 0~1**이다. 사용자가 화면에서 본 그
 * 방향이고(브라우저가 EXIF 회전을 적용해 그린 것), 서버의 영역 마커 합성이 `sharp.rotate()`
 * **뒤에** 일어나므로 두 좌표계가 그렇게 맞춰져 있다. 원본 픽셀 격자로 환산해서 보내면 **세로
 * 촬영 사진에서 전혀 다른 자리에 사각형이 그려진다**(근거: 서버 `drawRegionMarker` JSDoc).
 *
 * ⚠️ 키는 `{x,y,w,h}`다 — 같은 화면에 사는 `BoundingBox`는 `{x,y,width,height}`이고,
 * 영역에 `width`/`height`를 실어 보내면 서버가 400으로 거부한다(`AnalysisRegionValidator`).
 */
import { MIN_ANALYSIS_REGION_SIDE, type AnalysisRegion } from '@safetyhub/shared';

/** 표시 영역 안의 정규화 좌표(0~1) 한 점. */
export interface NormalizedPoint {
  x: number;
  y: number;
}

/**
 * 영역의 최소 변 길이(정규화) — 이보다 작으면 「그린 것」으로 보지 않는다.
 *
 * ⚠️ **값을 여기서 선언하지 않는다 — shared `MIN_ANALYSIS_REGION_SIDE`가 정본이다**(2026-10-05).
 * 서버 판정자(`isValidAnalysisRegion`)가 같은 상수로 최소 변을 거부하므로, 여기에 숫자를 따로
 * 적으면 둘이 갈리는 날 **화면이 그려 준 영역을 저장할 때 400을 받거나**(web이 느슨할 때)
 * **서버가 받을 크기를 화면이 먼저 막는다**(web이 엄격할 때). 이름을 `MIN_REGION_SIDE`로 유지하는
 * 것은 이 디렉토리 안의 기존 호출부·spec을 그대로 두기 위해서다.
 *
 * 2%인 근거는 그 상수 JSDoc에 있다(`InteractiveHazardEditor`의 리사이즈 최소 크기 `minSize = 0.02`와
 * 같은 값 — 같은 사진 위 두 화면이 「더 줄일 수 없는 크기」를 다르게 가지면 사용자가 혼동한다).
 *
 * ⚠️ 이 값은 **탭(클릭) 한 번과 드래그를 가르는 축도 겸한다** — 탭은 두 점이 거의 같아
 * `w`·`h`가 0에 가깝다. 즉 아래 `regionFromDragPoints`가 `null`을 돌려주는 것은 「실수로 눌렀다」를
 * 영역으로 저장하지 않기 위한 것이다.
 */
export const MIN_REGION_SIDE = MIN_ANALYSIS_REGION_SIDE;

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * 포인터 이벤트의 화면 좌표를 **표시 영역 기준 정규화 0~1**로 바꾼다.
 *
 * `rect`는 **사진이 실제로 그려진 사각형**의 `getBoundingClientRect()`여야 한다 —
 * `object-contain` 여백(letterbox)을 포함한 컨테이너가 아니다. 여백까지 포함한 사각형을 넘기면
 * 좌표가 여백만큼 밀려 **사진 밖을 가리키는 영역**이 만들어진다. 호출부(`AnalysisRegionEditor`)는
 * 오버레이를 `left: offsetX, top: offsetY, width/height = 표시 크기`로 두어 그 요소의 rect가
 * 곧 사진 사각형이 되게 한다(그리기 메모 `usePhotoAnnotation.getCanvasCoords`와 같은 규약).
 *
 * 포인터가 사진 밖으로 나가도 **clamp해서 0~1 안에 둔다** — 드래그가 사진 경계를 넘는 것은
 * 흔하고, 그때 영역이 사진을 벗어나면 서버 판정자가 `x+w ≤ 1`로 거부한다.
 */
export function toNormalizedPoint(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): NormalizedPoint {
  if (rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 };
  return {
    x: clamp01((clientX - rect.left) / rect.width),
    y: clamp01((clientY - rect.top) / rect.height),
  };
}

/**
 * 드래그의 두 점으로 영역을 만든다 — 어느 방향으로 끌어도 같은 사각형이 나온다.
 *
 * 오른쪽-아래로만 끌 수 있게 하면 사용자가 왼쪽-위로 끌었을 때 아무 일도 안 일어나고, 그것은
 * 「반응이 없다」로 보인다. 그래서 두 점의 min/max로 정규화한다.
 *
 * 최소 크기(`MIN_REGION_SIDE`)보다 작으면 **`null`** — 탭 한 번이나 손떨림을 영역으로 저장하지
 * 않는다(그런 값은 서버도 거부한다).
 */
export function regionFromDragPoints(
  a: NormalizedPoint,
  b: NormalizedPoint,
): AnalysisRegion | null {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.abs(a.x - b.x);
  const h = Math.abs(a.y - b.y);

  if (w < MIN_REGION_SIDE || h < MIN_REGION_SIDE) return null;

  return { x, y, w, h };
}

/**
 * 영역을 사진 안으로 **이동**한다(크기 보존).
 *
 * 경계에 닿으면 더 가지 않고 멈춘다 — 크기를 줄여 가며 밀려 들어가면 사용자가 「옮겼는데
 * 작아졌다」를 보게 된다.
 */
export function moveRegion(
  region: AnalysisRegion,
  deltaX: number,
  deltaY: number,
): AnalysisRegion {
  return {
    ...region,
    x: Math.max(0, Math.min(1 - region.w, region.x + deltaX)),
    y: Math.max(0, Math.min(1 - region.h, region.y + deltaY)),
  };
}

/** 리사이즈 핸들 — 네 모서리. */
export type RegionCorner = 'tl' | 'tr' | 'bl' | 'br';

/**
 * 모서리를 끌어 영역 크기를 바꾼다.
 *
 * 끄는 모서리의 **대각 꼭짓점을 고정**하고 두 점으로 다시 사각형을 만든다 — 그래야 모서리를
 * 반대쪽으로 지나쳐 끌어도(뒤집어도) 자연스럽게 따라온다. 최소 크기 미달이면 **직전 영역을
 * 그대로 돌려준다**(`null`이 아니다 — 리사이즈 중에 영역이 사라지면 안 된다).
 */
export function resizeRegion(
  region: AnalysisRegion,
  corner: RegionCorner,
  point: NormalizedPoint,
): AnalysisRegion {
  const anchor: NormalizedPoint = {
    x: corner === 'tl' || corner === 'bl' ? region.x + region.w : region.x,
    y: corner === 'tl' || corner === 'tr' ? region.y + region.h : region.y,
  };

  return regionFromDragPoints(anchor, point) ?? region;
}
